import { QueryClient } from '@tanstack/react-query';

import { DefaultFile } from '@components/file-field/types';
import { ColumnEditValue, EditOption, FileEditValue } from '@components/table/types';

import { AttributeFieldType } from 'api/attributes/requests';
import type { CompanyTraveller } from 'api/company-traveller/requests';
import { waitForElasticRefresh } from 'api/hooks';
import {
  ListResponse,
  multiOptionValues as AttributeMultiOptionValue,
  TravellerAttribute,
  TravellerJourneyListItem,
} from 'api/types';

export type AttributeEditPatch = {
  attributeShortId: string;
  attributeTitle: string;
  attributeType: AttributeFieldType;
  value: ColumnEditValue;
  options?: EditOption[];
};

type BaseAttribute = {
  attributeShortId: string;
  attributeTitle: string;
  attributeType: AttributeFieldType;
};

const buildValueAttribute = (base: BaseAttribute, value: any): TravellerAttribute => ({
  ...base,
  value,
  files: undefined as never,
  multiOptionValues: undefined as never,
});

const buildFileAttribute = (base: BaseAttribute, files: DefaultFile[]): TravellerAttribute => ({
  ...base,
  value: undefined as never,
  files,
  multiOptionValues: undefined as never,
});

const buildMultiOptionAttribute = (
  base: BaseAttribute,
  multiOptionValues: AttributeMultiOptionValue[]
): TravellerAttribute => ({
  ...base,
  value: undefined as never,
  files: undefined as never,
  multiOptionValues,
});

const findOptionLabel = (options: EditOption[] | undefined, value: string): string | undefined =>
  options?.find((option) => option.value === value)?.label;

type PatchedFile = DefaultFile & { srcConverted?: string };

const buildFileEntryFiles = (
  existingFiles: DefaultFile[],
  fileValue: FileEditValue,
  isMulti: boolean
): PatchedFile[] => {
  const addedEntries: PatchedFile[] = fileValue.addedFiles.map((file) => ({
    id: '',
    name: file.name,
    src: '',
    srcConverted: '',
  }));

  const removedIds = fileValue.removedFileShortIds.map(String);
  const remaining = existingFiles.filter((file) => !removedIds.includes(String(file.id)));

  if (!isMulti) {
    return addedEntries.length > 0 ? addedEntries : remaining;
  }

  return remaining.concat(addedEntries);
};

export const applyAttributeEditToAttributes = (
  attributes: TravellerAttribute[],
  patch: AttributeEditPatch
): TravellerAttribute[] => {
  const existing = attributes.find(
    (attribute) => attribute.attributeShortId === patch.attributeShortId
  );
  const base: BaseAttribute = {
    attributeShortId: patch.attributeShortId,
    attributeTitle: patch.attributeTitle,
    attributeType: patch.attributeType,
  };

  let nextEntry: TravellerAttribute | null;

  switch (patch.attributeType) {
    case 'email':
    case 'phone_number':
    case 'text':
    case 'paragraph': {
      const value = patch.value as string;
      nextEntry = value === '' ? null : buildValueAttribute(base, value);
      break;
    }
    case 'date': {
      nextEntry = buildValueAttribute(base, patch.value);
      break;
    }
    case 'number':
    case 'decimal': {
      const value = patch.value as string;
      // The editor sends a string, but the list renders a number - putting the
      // string in the cache would leave the cell formatted differently until
      // the refetch lands. An empty field clears the attribute; a zero does not.
      nextEntry = value === '' ? null : buildValueAttribute(base, Number(value));
      break;
    }
    case 'single_select': {
      const value = patch.value as string;
      if (value === '') {
        nextEntry = null;
        break;
      }
      const label = findOptionLabel(patch.options, value) ?? String(value);
      nextEntry = buildValueAttribute(base, { shortId: value, label });
      break;
    }
    case 'user': {
      const value = patch.value as string;
      if (value === '') {
        nextEntry = null;
        break;
      }
      const name = findOptionLabel(patch.options, value) ?? '';
      nextEntry = buildValueAttribute(base, { shortId: value, name, email: '' });
      break;
    }
    case 'multi_select': {
      const shortIds = patch.value as string[];
      if (shortIds.length === 0) {
        nextEntry = null;
        break;
      }
      nextEntry = buildMultiOptionAttribute(
        base,
        shortIds.map((shortId) => ({
          shortId,
          value: '',
          label: findOptionLabel(patch.options, shortId) ?? shortId,
        }))
      );
      break;
    }
    case 'file':
    case 'file_multi': {
      const fileValue = patch.value as FileEditValue;
      const files = buildFileEntryFiles(
        existing?.files ?? [],
        fileValue,
        patch.attributeType === 'file_multi'
      );
      nextEntry = files.length === 0 ? null : buildFileAttribute(base, files);
      break;
    }
    default:
      nextEntry = existing ?? null;
  }

  if (nextEntry === null) {
    return existing
      ? attributes.filter((attribute) => attribute.attributeShortId !== patch.attributeShortId)
      : [...attributes];
  }

  return existing
    ? attributes.map((attribute) =>
        attribute.attributeShortId === patch.attributeShortId
          ? (nextEntry as TravellerAttribute)
          : attribute
      )
    : [...attributes, nextEntry];
};

export const applyBaseFieldEdit = <T extends { email: string; phone: string }>(
  item: T,
  field: 'email' | 'phone',
  value: string
): T => ({ ...item, [field]: value });

export const readCachedAttribute = <Item>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  matchItem: (item: Item) => boolean,
  getAttributes: (item: Item) => TravellerAttribute[],
  attributeShortId: string
): TravellerAttribute | undefined => {
  const cachedItem = queryClient.getQueryData<Item[]>(queryKey)?.find(matchItem);
  return cachedItem
    ? getAttributes(cachedItem).find((attribute) => attribute.attributeShortId === attributeShortId)
    : undefined;
};

// One field's inline-edit history, which is what the reconcile loop needs to
// read a refetch correctly.
//
// Asking "is the cache still on the value this edit replaced" answers it wrong
// twice over. Elasticsearch can lag by more than one edit and hand back a value
// from further back than the one this edit replaced - read as "the server has
// caught up", that abandoned the optimistic value and left the cell showing
// something the user never picked. And a second edit to the same field left two
// loops alive, each reapplying its own value over the other's.
//
// So a field remembers the values it has moved off, and each edit takes a
// generation: the cache is stale while it holds any of those older values, and a
// loop stops as soon as a newer edit has taken the field over. Comparing against
// the history rather than against the value this edit wrote is also what keeps
// a file edit working, whose optimistic entry is a placeholder that never
// matches what the server eventually returns.
const REPLACED_VALUE_LIMIT = 5;

type FieldEditRecord = { generation: number; replacedValues: Array<string> };

const fieldEdits = new Map<string, FieldEditRecord>();

const serializeFieldValue = (value: unknown): string => JSON.stringify(value ?? null);

export type FieldEdit = {
  isSuperseded(): boolean;
  isStale(cachedValue: unknown): boolean;
};

export const startFieldEdit = (fieldKey: string, replacedValue: unknown): FieldEdit => {
  const record = fieldEdits.get(fieldKey) ?? { generation: 0, replacedValues: [] };
  const replaced = serializeFieldValue(replacedValue);

  record.generation += 1;
  record.replacedValues = record.replacedValues
    .filter((seen) => seen !== replaced)
    .concat(replaced)
    .slice(-REPLACED_VALUE_LIMIT);
  fieldEdits.set(fieldKey, record);

  const generation = record.generation;

  return {
    isSuperseded: () => record.generation !== generation,
    isStale: (cachedValue) => record.replacedValues.includes(serializeFieldValue(cachedValue)),
  };
};

export const patchTravellerJourneyListCache = (
  queryClient: QueryClient,
  journeyShortId: string,
  travellerJourneyShortId: string,
  mapItem: (item: TravellerJourneyListItem) => TravellerJourneyListItem
): void => {
  queryClient.setQueryData<TravellerJourneyListItem[]>(['travellers', journeyShortId], (old) =>
    old?.map((item) => (item.shortId === travellerJourneyShortId ? mapItem(item) : item))
  );
};

export const patchTravellerJourneyListCacheByCompanyTraveller = (
  queryClient: QueryClient,
  journeyShortId: string,
  companyTravellerShortId: string,
  mapItem: (item: TravellerJourneyListItem) => TravellerJourneyListItem
): void => {
  queryClient.setQueryData<TravellerJourneyListItem[]>(['travellers', journeyShortId], (old) =>
    old?.map((item) =>
      item.companyTravellerShortId === companyTravellerShortId ? mapItem(item) : item
    )
  );
};

type CompanyTravellerListCacheData = ListResponse<CompanyTraveller> | CompanyTraveller[];

export const patchCompanyTravellerListCaches = (
  queryClient: QueryClient,
  companyTravellerShortId: string,
  mapItem: (traveller: CompanyTraveller) => CompanyTraveller
): void => {
  queryClient.setQueriesData<CompanyTravellerListCacheData>(
    { queryKey: ['company-travellers'] },
    (old) => {
      if (!old) {
        return old;
      }

      const mapResults = (results: CompanyTraveller[]) =>
        results.map((traveller) =>
          traveller.shortId === companyTravellerShortId ? mapItem(traveller) : traveller
        );

      // The 'full' variant (loadCompanyTravellersFull) caches a plain array,
      // while the paginated variant caches a {count, results} ListResponse -
      // both live under the same 'company-travellers' key prefix.
      if (Array.isArray(old)) {
        return mapResults(old);
      }
      if (Array.isArray(old.results)) {
        return { ...old, results: mapResults(old.results) };
      }
      return old;
    }
  );
};

export const reconcileAfterElasticRefresh = ({
  refetch,
  isStale,
  isSuperseded = () => false,
  reapply,
  retryDelayMs = 2000,
  // The search index on a busy environment can lag well past a few seconds,
  // and once the loop dies the next refetch parks whatever the index holds.
  maxRetries = 10,
}: {
  refetch: () => Promise<unknown>;
  isStale: () => boolean;
  isSuperseded?: () => boolean;
  reapply: () => void;
  retryDelayMs?: number;
  maxRetries?: number;
}): void => {
  // Every refetch overwrites the optimistic value with whatever the search
  // index holds, so each one has to be followed by the same staleness check -
  // a retry that only refetches leaves a slow-to-index edit (a file upload,
  // typically) looking like it silently reverted.
  const settle = async (retriesLeft: number): Promise<void> => {
    // A newer edit to the same field owns it now, and its own loop outlives
    // this one - reapplying here would put the superseded value back.
    if (isSuperseded()) {
      return;
    }

    await refetch();

    if (isSuperseded() || !isStale()) {
      return;
    }

    reapply();

    if (retriesLeft > 0) {
      setTimeout(() => {
        settle(retriesLeft - 1).catch(() => {});
      }, retryDelayMs);
    }
  };

  waitForElasticRefresh(async () => {
    try {
      await settle(maxRetries);
    } catch {
      // A failed background reconciliation must not surface as an unhandled
      // rejection - the optimistic patch already stands, so there's nothing to undo.
    }
  }).catch(() => {});
};
